// /account: the person's own settings, laid out like Admin and the Guide: a rail on the left, hairline sections on the right.

import { useEffect, useState } from 'react';
import { mustSetUp } from '../api';
import { Link } from '../router';
import { useStore } from '../store';
import { BranchedMenu } from '../ui/BranchedMenu';
import { CliDevicesCard } from './CliDevices';
import { DeleteAccount, LinksCard, PasswordForm } from './Dashboard';
import { PlanBanner, PlanCard } from './PlanCard';
import { DashLayout, ago, fullDate } from './Shell';
import { Section } from '../ui/Section';
import { Tag } from '../ui/Tag';
import { Toasts } from '../ui/Toasts';

const ALL_RAIL: { label: string; items: [string, string][] }[] = [
  { label: 'Account', items: [['profile', 'Profile'], ['password', 'Password']] },
  { label: 'Preferences', items: [['links', 'Open links'], ['devices', 'Command line']] },
  { label: 'Billing', items: [['plan', 'Plan']] },
  { label: 'Danger zone', items: [['delete', 'Delete account']] },
];

export function Account() {
  const me = useStore((s) => s.me)!;
  const [active, setActive] = useState('profile');
  // sections that render nothing (e.g. no plan card yet) leave the rail
  const [empty, setEmpty] = useState<Set<string>>(new Set());
  // before setup is done: the plan and the account only (the rest needs the machine)
  const early = mustSetUp(me);
  const needsMachine = new Set(['links', 'devices']);
  const RAIL = ALL_RAIL.map((g) => ({ ...g, items: g.items.filter(([id]) => !empty.has(id) && !(early && needsMachine.has(id))) })).filter((g) => g.items.length);
  useEffect(() => {
    const root = document.querySelector('.acct-content');
    if (!root) return;
    const scan = () => {
      const next = new Set<string>();
      root.querySelectorAll<HTMLElement>(':scope > .acct-sec').forEach((el) => { if (el.childElementCount === 0) next.add(el.id); });
      setEmpty((prev) => (prev.size === next.size && [...next].every((x) => prev.has(x)) ? prev : next));
    };
    scan();
    const mo = new MutationObserver(scan);
    mo.observe(root, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    const els = RAIL.flatMap((g) => g.items.map(([id]) => document.getElementById(id))).filter((e): e is HTMLElement => !!e);
    const io = new IntersectionObserver(
      (entries) => {
        const seen = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (seen[0]) setActive(seen[0].target.id);
      },
      { rootMargin: '0px 0px -70% 0px' },
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, [empty]);

  return (
    <DashLayout title="Account" subtitle={early ? 'Your plan and your account. Everything else opens once your cloud machine is set up.' : me.email}>
      <PlanBanner me={me} />
      <div className="admin acct" data-testid="account-page">
        <nav className="admin-rail" aria-label="Account sections" data-testid="account-rail">
          <BranchedMenu
            label="Account sections"
            active={active}
            onSelect={setActive}
            sections={RAIL.map((g) => ({ label: g.label, children: g.items.map(([value, label]) => ({ value, label, href: `#${value}` })) }))}
          />
        </nav>
        <div className="admin-content acct-content">
          <Section
            id="profile"
            title="Profile"
            hint="The email you sign in with."
            testid="account-card"
          >
            <dl className="acct-kv">
              <div>
                <dt>Email</dt>
                <dd data-testid="account-email">{me.email}</dd>
              </div>
              <div>
                <dt>Role</dt>
                <dd>
                  <Tag tone={me.role === 'admin' ? 'accent' : 'neutral'} data-testid="account-role">
                    {me.role}
                  </Tag>
                </dd>
              </div>
              {me.created && (
                <div>
                  <dt>Member since</dt>
                  <dd title={fullDate(me.created)}>{new Date(me.created).toLocaleDateString(undefined, { dateStyle: 'medium' })}</dd>
                </div>
              )}
              {me.lastLogin && (
                <div>
                  <dt>Last sign-in</dt>
                  <dd title={fullDate(me.lastLogin)}>{ago(me.lastLogin)}</dd>
                </div>
              )}
            </dl>
          </Section>

          <Section id="password" title="Password" hint="Use at least 10 characters. You stay signed in on this browser.">
            <PasswordForm email={me.email} bare />
          </Section>

          {!early && (
            <div className="acct-sec" id="links">
              <LinksCard me={me} />
            </div>
          )}

          {!early && (
            <div className="acct-sec" id="devices">
              <CliDevicesCard />
            </div>
          )}

          <div className="acct-sec" id="plan">
            <PlanCard me={me} />
          </div>

          <Section id="delete" title="Delete account" hint="Deletes your plan, cloud machine and everything on it. This can’t be undone." danger>
            <DeleteAccount me={me} />
          </Section>
          {early && (
            <p className="muted small">
              <Link to="/start">← Back to setting up</Link>
            </p>
          )}
        </div>
      </div>
      <Toasts />
    </DashLayout>
  );
}
